import { onMounted, onUnmounted, computed } from 'vue';

/**
 * Composable para gestionar atajos de teclado globales
 * @param {Object} callbacks - Objeto con callbacks para cada acción
 * @param {Function} callbacks.onSave - Callback para guardar (Ctrl/Cmd + S)
 * @param {Function} callbacks.onNew - Callback para nueva nota (Ctrl/Cmd + N)
 * @param {Function} callbacks.onList - Callback para ver lista (Ctrl/Cmd + L)
 * @param {Function} callbacks.onCancel - Callback para cancelar (Esc)
 * @param {Boolean} enabled - Si los atajos están habilitados (default: true)
 */
export function useKeyboardShortcuts(callbacks = {}, enabled = true) {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    
    const shortcuts = {
        save: { key: 's', ctrl: true, description: 'Guardar nota' },
        new: { key: 'n', ctrl: true, description: 'Nueva nota' },
        list: { key: 'l', ctrl: true, description: 'Ver lista de notas' },
        cancel: { key: 'Escape', ctrl: false, description: 'Cancelar' }
    };

    const handleKeyDown = (event) => {
        if (!enabled) return;

        const isCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey;
        const key = event.key.toLowerCase();

        // Ctrl/Cmd + S - Guardar
        if (isCtrlOrCmd && key === 's') {
            event.preventDefault();
            if (callbacks.onSave) {
                callbacks.onSave();
                // No mostramos mensaje porque ya hay indicador de guardado
            }
            return;
        }

        // Ctrl/Cmd + N - Nueva nota
        if (isCtrlOrCmd && key === 'n') {
            event.preventDefault();
            if (callbacks.onNew) {
                callbacks.onNew();
                showShortcutToast('📝 Nueva nota');
            }
            return;
        }

        // Ctrl/Cmd + L - Ver lista
        if (isCtrlOrCmd && key === 'l') {
            event.preventDefault();
            if (callbacks.onList) {
                callbacks.onList();
                showShortcutToast('📋 Lista de notas');
            }
            return;
        }

        // Esc - Cancelar (solo si hay callback)
        if (key === 'escape') {
            // NO prevenir default para que ESC cierre modales normalmente
            if (callbacks.onCancel) {
                callbacks.onCancel();
            }
            return;
        }
    };

    const showShortcutToast = (message) => {
        // Crear toast temporal
        const toast = document.createElement('div');
        toast.className = 'keyboard-shortcut-toast';
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            bottom: 30px;
            right: 30px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 12px 20px;
            border-radius: 12px;
            font-weight: 500;
            font-size: 14px;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
            z-index: 10000;
            animation: slideInRight 0.3s ease, fadeOut 0.3s ease 2.7s;
            pointer-events: none;
        `;
        
        document.body.appendChild(toast);
        
        setTimeout(() => {
            toast.remove();
        }, 3000);
    };

    const getShortcutKey = (action) => {
        const shortcut = shortcuts[action];
        if (!shortcut) return '';
        
        const modifier = isMac ? '⌘' : 'Ctrl';
        return shortcut.ctrl ? `${modifier} + ${shortcut.key.toUpperCase()}` : shortcut.key;
    };

    const getShortcutList = computed(() => {
        return Object.entries(shortcuts).map(([action, config]) => ({
            action,
            key: getShortcutKey(action),
            description: config.description
        }));
    });

    onMounted(() => {
        if (enabled) {
            document.addEventListener('keydown', handleKeyDown);
            
            // Añadir estilos de animación si no existen
            if (!document.getElementById('keyboard-shortcuts-styles')) {
                const style = document.createElement('style');
                style.id = 'keyboard-shortcuts-styles';
                style.textContent = `
                    @keyframes slideInRight {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                    
                    @keyframes fadeOut {
                        from {
                            opacity: 1;
                        }
                        to {
                            opacity: 0;
                        }
                    }
                `;
                document.head.appendChild(style);
            }
        }
    });

    onUnmounted(() => {
        document.removeEventListener('keydown', handleKeyDown);
    });

    return {
        shortcuts,
        getShortcutKey,
        getShortcutList,
        isMac
    };
}
